// Admin console: feature-flag matrix (Phase 3, concept doc §4.6/§7). Renders
// the §3.2 resolution across every (tenant, flag) pair, with the source of
// each cell visible. The literal grid only stays readable at small plan/flag
// counts, so the same screen offers a filtered list mode — "show only tenants
// with an override" plus a name filter — rather than one giant table.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { adminApi } from '../api/adminClient';
import type { FlagCell } from '../api/types';
import { useAsync } from '../lib/hooks';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { Badge, Button, Card } from '../components/ui';
import { Field } from '../components/Field';
import { useToast } from '../components/Toast';
import { IconClose, IconLayers, IconSearch } from '../components/Icons';

interface EditTarget {
  tenantId: string;
  tenantName: string;
  field: string;
  cell: FlagCell;
}

function sourceTone(source: FlagCell['source']): 'accent' | 'info' | 'default' {
  if (source === 'override') return 'accent';
  if (source === 'features') return 'info';
  return 'default';
}

function displayValue(v: unknown): string {
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'boolean') return v ? '✓' : '✗';
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

export function AdminFeatureFlags() {
  const { t } = useTranslation();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [overriddenOnly, setOverriddenOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const matrix = useAsync(() => adminApi.adminFeatureFlags(q || undefined, overriddenOnly), [q, overriddenOnly]);

  const clearOverride = async (tenantId: string, field: string) => {
    setBusy(true);
    try {
      await adminApi.adminSetFlagOverride(tenantId, field, null, true);
      toast.success(t('admin.flags.cleared'));
      matrix.reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title={t('admin.flags.title')} subtitle={t('admin.flags.subtitle')} />

      <Card>
        <div className="row" style={{ padding: '12px 16px', gap: 16, flexWrap: 'wrap' }}>
          <span className="input" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 220 }}>
            <IconSearch width={16} height={16} />
            <input
              style={{ border: 'none', outline: 'none', flex: 1, background: 'transparent' }}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('admin.customers.filterPlaceholder')}
              aria-label={t('admin.customers.filterPlaceholder')}
            />
          </span>
          <label className="row" style={{ gap: 8 }}>
            <input
              type="checkbox"
              checked={overriddenOnly}
              onChange={(e) => setOverriddenOnly(e.target.checked)}
            />
            {t('admin.flags.overriddenOnly')}
          </label>
        </div>

        <ApiState status={matrix.status} error={matrix.error} data={matrix.status === 'error' ? null : matrix.data} onRetry={matrix.reload}>
          {(d) =>
            d.items.length === 0 ? (
              <EmptyState
                title={overriddenOnly ? t('admin.flags.emptyOverridden') : t('admin.flags.empty')}
                icon={<IconLayers width={26} height={26} />}
              />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>{t('common.name')}</th>
                      <th>{t('admin.customers.colPlan')}</th>
                      {d.columns.map((c) => (
                        <th key={c}>{t(`admin.fields.${c}`, c)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.items.map((row) => (
                      <tr key={row.tenantId}>
                        <td className="cell-strong">
                          <Link to={`/customers/${encodeURIComponent(row.tenantId)}`}>{row.tenantName}</Link>
                        </td>
                        <td>{row.planName ? `${row.planName} (v${row.planVersion})` : t('admin.customers.noPlan')}</td>
                        {d.columns.map((c) => {
                          const cell = row.cells[c];
                          if (!cell) return <td key={c} className="muted">—</td>;
                          return (
                            <td key={c}>
                              <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                                <span>{displayValue(cell.value)}</span>
                                <Badge tone={sourceTone(cell.source)}>{t(`admin.source.${cell.source}`)}</Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={busy}
                                  onClick={() =>
                                    setEditing({ tenantId: row.tenantId, tenantName: row.tenantName, field: c, cell })
                                  }
                                  title={t('admin.flags.overrideHint')}
                                >
                                  {t('admin.flags.override')}
                                </Button>
                                {cell.source === 'override' ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={busy}
                                    onClick={() => clearOverride(row.tenantId, c)}
                                    title={t('admin.flags.clearHint')}
                                  >
                                    {t('admin.flags.clear')}
                                  </Button>
                                ) : null}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </ApiState>
      </Card>

      {editing ? (
        <SetOverrideDialog
          target={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success(t('admin.flags.overrideSaved'));
            matrix.reload();
          }}
        />
      ) : null}
    </>
  );
}

// SetOverrideDialog edits ONE flag for ONE tenant. The input shape follows the
// current value's type (boolean -> checkbox, array -> comma-separated list,
// everything else -> text), so the value sent back keeps the type the plan
// defines rather than turning every flag into a string.
function SetOverrideDialog({
  target,
  onClose,
  onSaved,
}: {
  target: EditTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isBool = typeof target.cell.value === 'boolean';
  const isList = Array.isArray(target.cell.value);
  const [boolValue, setBoolValue] = useState<boolean>(isBool ? (target.cell.value as boolean) : false);
  const [textValue, setTextValue] = useState<string>(
    isList ? (target.cell.value as unknown[]).join(', ') : String(target.cell.value ?? ''),
  );
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    let value: unknown = textValue;
    if (isBool) value = boolValue;
    else if (isList) value = textValue.split(',').map((s) => s.trim()).filter(Boolean);
    try {
      await adminApi.adminSetFlagOverride(target.tenantId, target.field, value);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="flag-title">
        <div className="row between">
          <h2 className="dialog__title" id="flag-title">
            {t('admin.flags.overrideTitle', { name: target.tenantName })}
          </h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <IconClose width={16} height={16} />
          </button>
        </div>
        <div style={{ marginTop: 16 }}>
          <Field
            label={t(`admin.fields.${target.field}`, target.field)}
            hint={isList ? t('admin.plans.listHint') : undefined}
            error={error}
          >
            {(p) =>
              isBool ? (
                <input type="checkbox" {...p} checked={boolValue} onChange={(e) => setBoolValue(e.target.checked)} />
              ) : (
                <input className="input" {...p} value={textValue} autoFocus onChange={(e) => setTextValue(e.target.value)} />
              )
            }
          </Field>
        </div>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
